import { APIService, type RequestBody } from '../../api/apiService';
import type AuthenticatedUser from '../../interfaces/AuthenticatedUser';
import { useAppStore } from '../../store/store';
import type User from '../../interfaces/User';
import type {
  CreateLoginTokenPayload,
  CreateLoginTokenResponse,
  CreateUserPayload,
  EnterOnBehalfOfUserPayload,
  EnterOnBehalfOfUserResponse,
  ExitOnBehalfOfUserResponse,
  GetUserOtpResponse,
  GetUsersPayload,
  GetUsersResponse,
  LoginToken,
  LoginV2Response,
  LoginWithTotpPayload,
  LoginWithTotpResponse,
  PasskeyAdminRemoveAllResponse,
  PasskeyAdminRemoveCredentialResponse,
  PasskeyAdminStatusResponse,
  PasskeyAuthCompletePayload,
  PasskeyAuthOptionsPayload,
  PasskeyAuthOptionsResponse,
  PasskeyAuthTokensResponse,
  PasskeyMutationResponse,
  PasskeyRegisterCompletePayload,
  PasskeyRegisterCompleteResponse,
  PasskeyRegisterOptionsResponse,
  PasskeyRemoveResponse,
  PasskeyRenamePayload,
  PasskeyStatusResponse,
  PasskeyTogglePasswordlessPayload,
  PasswordlessAuthCompletePayload,
  PasswordlessAuthOptionsResponse,
  PasswordlessOptionsPayload,
  TotpAdminDisableResponse,
  TotpAdminGrantExemptPayload,
  TotpAdminGrantExemptResponse,
  TotpAdminRevokeExemptResponse,
  TotpAdminStatusResponse,
  TotpDisablePayload,
  TotpForcedSetupPayload,
  TotpForcedSetupVerifyPayload,
  TotpForcedSetupVerifyResponse,
  TotpRegenerateBackupCodesPayload,
  TotpRegenerateBackupCodesResponse,
  TotpRemoveFromUserResponse,
  TotpSetupResponse,
  TotpStatusResponse,
  TotpVerifyPayload,
  TotpVerifyResponse,
  TransferOwnershipPayload,
  TransferOwnershipResponse,
  UpdateLoginTokenStatusPayload,
} from '../../interfaces/UserApiService';

/** Request config: omit the Bearer header (required for anonymous WebAuthn auth endpoints). */
const REQUEST_NO_AUTH = { skipAuth: true as const };

export class UserService extends APIService {
  /**
   * @desc Get user info
   * @return {Promise<*>
   *     }
   */
  getUserInfo(): Promise<AuthenticatedUser> {
    return this.request('/user/auth/data', 'GET');
  }

  /**
   * Sends a request to generate an OTP for the user.
   *
   * @param {RequestBody} payload - The payload containing user information required to generate the OTP.
   * @return {Promise<any>} - A promise that resolves to the server's response.
   */
  requestUserOtp(payload: RequestBody): Promise<GetUserOtpResponse> {
    return this.request('/user/otp/v2', 'POST', payload);
  }

  /**
   * Authenticates a user using provided credentials (OTP).
   * Returns either token/rToken or totpRequired + partialAuthToken when 2FA is enabled.
   *
   * @param payload - User login credentials (username, code, tenantUrl).
   * @return Promise resolving to success response or TOTP-required response.
   */
  login(payload: RequestBody): Promise<LoginV2Response> {
    return this.request('/user/auth/v2', 'POST', payload);
  }

  /**
   * Starts the mandatory (forced) TOTP setup flow after a `403 TOTP_SETUP_REQUIRED` login response.
   * Anonymous endpoint: authenticated via the `partialAuthToken` in the body, not a Bearer header.
   *
   * @param payload - The partial auth token from the 403 error.
   * @return Promise resolving with the TOTP secret/URI to render as a QR code.
   */
  totpForcedSetup(payload: TotpForcedSetupPayload): Promise<TotpSetupResponse> {
    return this.request(
      '/user/auth/totp/setup',
      'POST',
      { ...payload },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /**
   * Verifies the code for the mandatory TOTP setup flow and completes auto-login.
   * Anonymous endpoint: authenticated via the `partialAuthToken` in the body.
   *
   * @param payload - The partial auth token and the 6-digit code from the authenticator app.
   * @return Promise resolving with full session tokens and one-time backup codes.
   */
  totpForcedSetupVerify(
    payload: TotpForcedSetupVerifyPayload,
  ): Promise<TotpForcedSetupVerifyResponse> {
    return this.request(
      '/user/auth/totp/setup/verify',
      'POST',
      { ...payload, code: String(payload.code) },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /**
   * Refresh the access token using the refresh token.
   *
   * @return {Promise<{token: string; rToken: string}>} A promise that resolves with new tokens.
   */
  refreshToken(): Promise<{
    token: string;
    rToken: string;
  }> {
    return this.request('/user/auth/rtoken', 'GET');
  }

  /**
   * Invalidate the current session on the server (server-side logout).
   * Revokes the access/refresh tokens so the refresh token can no longer mint new
   * tokens via /user/auth/rtoken. The current access token is attached automatically
   * as the Bearer header by the request interceptor.
   *
   * @param refreshToken - Current refresh token (rToken) to explicitly revoke.
   * @param allDevices - When true, revoke every session of the user (logout everywhere).
   * @return A promise that resolves once the session is invalidated.
   */
  logout(refreshToken?: string, allDevices = false): Promise<{ message: string }> {
    return this.request('/user/auth/logout', 'POST', { refreshToken, allDevices });
  }

  /**
   * @desc Get the list of users
   * @param payload
   * @return {Promise<* | undefined>}
   */
  getUsers(payload: GetUsersPayload): Promise<GetUsersResponse> {
    return this.request('/listusers', 'POST', { ...payload });
  }

  /**
   * @desc Get a specific user
   * @param id
   * @return {Promise<* | undefined>}
   */
  getUser(id: number): Promise<User> {
    return this.request(`/user/${id}`, 'GET');
  }

  /**
   * @desc Update a user
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createUser(payload: CreateUserPayload): Promise<User> {
    return this.request('/user', 'POST', { ...payload });
  }

  /**
   * @desc Update a user
   * @param id
   * @param payload
   * @return {Promise<* | undefined>}
   */
  updateUser(payload: User) {
    return this.request(`/user/${payload.id}`, 'PUT', { ...payload });
  }

  /**
   * @desc Change the status of users
   * @param ids
   * @param status
   * @return {Promise<* | undefined>}
   */
  changeUsersStatus(ids: number[], status: 0 | 1) {
    return this.request(`/user/status?value=${status}`, 'PUT', ids);
  }

  /**
   * @desc Delete users. With `transferToUserId`, calls DELETE /user/{id}?transferToUserId= for each id.
   * @param ids User ids to delete
   * @param transferToUserId Optional recipient for personal files before deletion
   * @return {Promise<* | undefined>}
   */
  deleteUsers(ids: number[], transferToUserId?: number) {
    if (transferToUserId != null && Number.isFinite(transferToUserId)) {
      const q = `?transferToUserId=${encodeURIComponent(String(transferToUserId))}`;
      return Promise.all(ids.map((id) => this.request(`/user/${id}${q}`, 'DELETE'))).then(
        () => undefined,
      );
    }
    return this.request(`/user`, 'DELETE', ids);
  }

  /**
   * @desc Delete user groups
   * @return {Promise<void}
   * @param id
   * @param groupIds
   */
  deleteUserGroups(id: number, groupIds: number[]): Promise<void> {
    return this.request(`/user/${id}/groups`, 'DELETE', groupIds);
  }

  /**
   * Get AIGrant URL
   *
   * @param {RequestBody} payload - The payload containing tenantUrl
   * @return {Promise<any>} A promise that resolves with the AIGrant URL response
   */
  getAIGrantURL(payload: RequestBody): Promise<any> {
    return this.request('/ai/getAIGrantURL', 'POST', payload);
  }

  /**
   * Get OAuth state and nonce from backend for CSRF protection
   *
   * @param payload - OAuth state request payload
   * @return {Promise<{state: string; nonce: string; expiresAt: string}>} A promise that resolves with state and nonce
   */
  getOAuthState(payload: {
    tenantUrl: string;
    provider: string;
    clientId: string;
    redirectUri: string;
    loginHint?: string;
  }): Promise<{ state: string; nonce: string; expiresAt: string }> {
    return this.request('/oauth/state', 'POST', payload);
  }

  /**
   * OAuth callback - exchange authorization code for tokens
   *
   * @param payload - OAuth callback payload
   * @return {Promise<{token: string; rToken: string}>} A promise that resolves with Filegrant tokens
   */
  oauthCallback(payload: {
    provider: string;
    clientId: string;
    code: string;
    redirectUri: string;
    tenantUrl: string;
    state: string;
  }): Promise<{ token: string; rToken: string }> {
    return this.request('/oauth/callback', 'POST', payload);
  }

  /**
   * Enter on behalf of a user
   *
   * @param payload - Enter on behalf of user payload
   * @return {Promise<EnterOnBehalfOfUserResponse>} A promise that resolves when entering on behalf of user is complete
   */
  enterOnBehalfOfUser(payload: EnterOnBehalfOfUserPayload): Promise<EnterOnBehalfOfUserResponse> {
    return this.request('/user/auth/impersonate', 'POST', { ...payload });
  }

  /**
   * Exit on behalf of a user
   *
   * @return {Promise<ExitOnBehalfOfUserResponse>} A promise that resolves when exiting on behalf of user is complete
   */
  exitOnBehalfOfUser(): Promise<ExitOnBehalfOfUserResponse> {
    return this.request('/user/auth/stop-impersonation', 'POST');
  }

  /**
   * Login with TOTP
   *
   * @param payload - Login with TOTP payload
   * @return {Promise<LoginWithTotpResponse>} A promise that resolves with the login with TOTP response
   */
  loginWithTotp(payload: LoginWithTotpPayload): Promise<LoginWithTotpResponse> {
    return this.request('/user/auth/totp', 'POST', { ...payload, code: String(payload.code) });
  }

  /** Headers for TOTP endpoints when using temporary token (login 2FA setup flow). */
  private getTotpHeaders(): { Authorization: string } | undefined {
    const token = useAppStore.getState().temporaryTotpToken;
    return token ? { Authorization: `Bearer ${token}` } : undefined;
  }

  /**
   * Setup TOTP
   *
   * @return {Promise<TotpSetupResponse>} A promise that resolves with the TOTP setup response
   */
  totpSetup(): Promise<TotpSetupResponse> {
    return this.request('/user/totp/setup', 'POST', undefined, this.getTotpHeaders());
  }

  /**
   * Verify TOTP code
   *
   * @param payload - Verify TOTP code payload
   * @return {Promise<TotpVerifyResponse>} A promise that resolves with the TOTP verify response
   */
  totpVerify(payload: TotpVerifyPayload): Promise<TotpVerifyResponse> {
    return this.request('/user/totp/verify', 'POST', { ...payload }, this.getTotpHeaders());
  }

  /**
   * Get TOTP status
   *
   * @return {Promise<TotpStatusResponse>} A promise that resolves with the TOTP status response
   */
  totpStatus(): Promise<TotpStatusResponse> {
    return this.request('/user/totp/status', 'GET', undefined, this.getTotpHeaders());
  }

  /**
   * Disable TOTP
   *
   * @param payload - Disable TOTP payload
   * @return {Promise<TotpDisableResponse>} A promise that resolves with the TOTP disable response
   */
  totpDisable(payload: TotpDisablePayload): Promise<any> {
    return this.request('/user/totp/disable', 'POST', { ...payload }, this.getTotpHeaders());
  }

  /**
   * Regenerate backup codes
   *
   * @param payload - Regenerate backup codes payload
   * @return {Promise<TotpRegenerateBackupCodesResponse>} A promise that resolves with the TOTP regenerate backup codes response
   */
  totpRegenerateBackupCodes(
    payload: TotpRegenerateBackupCodesPayload,
  ): Promise<TotpRegenerateBackupCodesResponse> {
    return this.request(
      '/user/totp/backup-codes/regenerate',
      'POST',
      { ...payload },
      this.getTotpHeaders(),
    );
  }

  /**
   * Remove TOTP from user
   *
   * @return {Promise<TotpRemoveFromUserResponse>} A promise that resolves with the TOTP remove from user response
   */
  totpRemoveFromUser(): Promise<TotpRemoveFromUserResponse> {
    return this.request('/user/totp/remove', 'DELETE', undefined, this.getTotpHeaders());
  }

  // --- Admin TOTP exemption (Bearer admin token) ---

  /**
   * Get detailed TOTP status for a user (admin).
   * GET /api/user/totp/admin/user/{userId}/status
   * @param userId - Target user id
   * @returns Detailed TOTP status (exempt, exemptUntil, reason, etc.)
   */
  totpAdminGetUserStatus(userId: number): Promise<TotpAdminStatusResponse> {
    return this.request(`/user/totp/admin/user/${userId}/status`, 'GET');
  }

  /**
   * Grant TOTP exemption for a user (admin).
   * POST /api/user/totp/admin/user/{userId}/exempt
   * @param userId - Target user id
   * @param payload - permanent, exemptionDays (if !permanent), reason (5–500 chars)
   */
  totpAdminGrantExempt(
    userId: number,
    payload: TotpAdminGrantExemptPayload,
  ): Promise<TotpAdminGrantExemptResponse> {
    return this.request(`/user/totp/admin/user/${userId}/exempt`, 'POST', { ...payload });
  }

  /**
   * Revoke TOTP exemption for a user (admin).
   * DELETE /api/user/totp/admin/user/{userId}/exempt
   * @param userId - Target user id
   */
  totpAdminRevokeExempt(userId: number): Promise<TotpAdminRevokeExemptResponse> {
    return this.request(`/user/totp/admin/user/${userId}/exempt`, 'DELETE');
  }

  /**
   * Disable TOTP for a user (admin). Removes 2FA for the target user.
   * POST /user/totp/admin/user/{userId}/disable
   * @param userId - Target user id
   */
  totpAdminDisableUser(userId: number): Promise<TotpAdminDisableResponse> {
    return this.request(`/user/totp/admin/user/${userId}`, 'DELETE');
  }

  /**
   * Transfer ownership
   * @param payload - Transfer ownership payload
   * @returns {Promise<TransferOwnershipResponse>} A promise that resolves with the transfer ownership response
   */
  transferOwnership(payload: TransferOwnershipPayload): Promise<TransferOwnershipResponse> {
    return this.request('/user/transfer-ownership', 'POST', { ...payload });
  }

  // --- Login Token management (Admin/SuperAdmin) ---

  getLoginTokens(userId: number): Promise<LoginToken[]> {
    return this.request(`/user/login-tokens/${userId}`, 'GET');
  }

  createLoginToken(payload: CreateLoginTokenPayload): Promise<CreateLoginTokenResponse> {
    return this.request('/user/login-tokens', 'POST', { ...payload });
  }

  updateLoginTokenStatus(tokenId: number, payload: UpdateLoginTokenStatusPayload): Promise<void> {
    return this.request(`/user/login-tokens/${tokenId}/status`, 'PATCH', { ...payload });
  }

  deleteLoginToken(tokenId: number): Promise<void> {
    return this.request(`/user/login-tokens/${tokenId}`, 'DELETE');
  }

  /** Current user's passkey policy and registered credentials (authenticated). */
  passkeyStatus(): Promise<PasskeyStatusResponse> {
    return this.request('/user/passkey/status', 'GET');
  }

  /** Starts passkey registration: returns WebAuthn creation options and `sessionId` (authenticated). */
  passkeyRegisterOptions(): Promise<PasskeyRegisterOptionsResponse> {
    return this.request('/user/passkey/register/options', 'POST');
  }

  /**
   * Finishes passkey registration after `startRegistration` in the browser (authenticated).
   *
   * @param payload - Serialized attestation, device label, session id, and transports.
   */
  passkeyRegisterComplete(
    payload: PasskeyRegisterCompletePayload,
  ): Promise<PasskeyRegisterCompleteResponse> {
    return this.request('/user/passkey/register/complete', 'POST', { ...payload });
  }

  /** Deletes one passkey credential for the current user (authenticated). */
  passkeyRemove(credentialId: string): Promise<PasskeyRemoveResponse> {
    return this.request(`/user/passkey/${encodeURIComponent(credentialId)}`, 'DELETE');
  }

  /** Renames the friendly device label for a credential (authenticated). */
  passkeyRename(
    credentialId: string,
    payload: PasskeyRenamePayload,
  ): Promise<PasskeyMutationResponse> {
    return this.request(`/user/passkey/${encodeURIComponent(credentialId)}/name`, 'PUT', {
      ...payload,
    });
  }

  /** Enables or disables passwordless use for a specific credential (authenticated). */
  passkeyTogglePasswordless(
    credentialId: string,
    payload: PasskeyTogglePasswordlessPayload,
  ): Promise<PasskeyMutationResponse> {
    return this.request(`/user/passkey/${encodeURIComponent(credentialId)}/passwordless`, 'PUT', {
      ...payload,
    });
  }

  /**
   * WebAuthn authentication options for completing login 2FA (no Bearer token; `partialAuthToken` in body).
   */
  passkeyAuthOptions(payload: PasskeyAuthOptionsPayload): Promise<PasskeyAuthOptionsResponse> {
    return this.request(
      '/user/auth/passkey/options',
      'POST',
      { ...payload },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /**
   * Exchanges a passkey assertion for session tokens after OTP + partial auth (no Bearer token).
   */
  passkeyAuthComplete(payload: PasskeyAuthCompletePayload): Promise<PasskeyAuthTokensResponse> {
    return this.request(
      '/user/auth/passkey/complete',
      'POST',
      { ...payload },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /**
   * Passwordless sign-in: WebAuthn request options for the given tenant (and optional username hint).
   * No Bearer token.
   */
  passkeyPasswordlessOptions(
    payload: PasswordlessOptionsPayload,
  ): Promise<PasswordlessAuthOptionsResponse> {
    return this.request(
      '/user/auth/passwordless/options',
      'POST',
      { ...payload },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /** Completes passwordless login with assertion + session id (no Bearer token). */
  passkeyPasswordlessComplete(
    payload: PasswordlessAuthCompletePayload,
  ): Promise<PasskeyAuthTokensResponse> {
    return this.request(
      '/user/auth/passwordless/complete',
      'POST',
      { ...payload },
      undefined,
      undefined,
      REQUEST_NO_AUTH,
    );
  }

  /** Admin: passkey status and credentials for a user (authenticated admin). */
  passkeyAdminUserStatus(userId: number): Promise<PasskeyAdminStatusResponse> {
    return this.request(`/user/passkey/admin/user/${userId}/status`, 'GET');
  }

  /** Admin: removes one passkey credential for a user (authenticated admin). */
  passkeyAdminRemoveCredential(
    userId: number,
    credentialId: string,
  ): Promise<PasskeyAdminRemoveCredentialResponse> {
    return this.request(
      `/user/passkey/admin/user/${userId}/credential/${encodeURIComponent(credentialId)}`,
      'DELETE',
    );
  }

  /** Admin: removes all passkeys for a user (authenticated admin). */
  passkeyAdminRemoveAll(userId: number): Promise<PasskeyAdminRemoveAllResponse> {
    return this.request(`/user/passkey/admin/user/${userId}/all`, 'DELETE');
  }
}
